import _ from 'lodash'
import React, { useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CSSTransition } from 'react-transition-group'
import { Checkbox, CheckboxProps, Icon, Input, Popup, Search, SearchResultProps } from 'semantic-ui-react'
import { SearchProps, SearchResultData } from 'semantic-ui-react/dist/commonjs/modules/Search/Search'
import { cancelPlacenameRequest } from '../../apis/PlaceServiceAPI'
import { useAppDispatch, useAppSelector } from '../../common/store'

import { LAYER_PLACE_NAMES_PATH } from '../../environment'
import AdvancedSearchComponent from './AdvancedSearchComponent'

import './PlacenamesSearchBar.css'
import {
  arePlacesLoading,
  getCandidatePlaces,
  getPlacenameSearchValue,
  getPlaces,
  PlacenameDataType,
  resetPlaceMapView,
  resetPlacenameData,
  updateCandidatePlacesData,
  updatePlacenameSearchValue,
} from './slice'

function PlacenamesSearchBar() {

  const [phoneticSearch, setPhoneticSearch] = useState<boolean>(false)
  const history = useHistory()
  const dispatch = useAppDispatch()
  const searchValue = useAppSelector(getPlacenameSearchValue)
  const isLoading = useAppSelector(arePlacesLoading)
  const placenamesSearchContainer = useRef<HTMLDivElement>(null)
  const candidatePlaces: PlacenameDataType[] = useAppSelector(getPlaces)
  const [minChar, setMinChar] = useState<number>(2)

  const performPlaceSearch = (value: string, phoneticSearch: boolean) => {
    handleSearchValueClear()
    dispatch(updatePlacenameSearchValue(value))
    dispatch(getCandidatePlaces({ phoneticSearch: phoneticSearch, searchFilters: value }))
  }

  const [debouncedSearch] = useState(() => {
    return _.debounce(performPlaceSearch, 500, {
      leading: false,
      trailing: true,
    })
  })

  //Build the results prop to be passed to the Search component
  const getResults = (places: PlacenameDataType[]) => {
    if (places) {
      return places.map(
        place => ({
          title: place.searchTerm,
          description: handleDelimitedValues(place.localities ? place.localities : '', 4, 'multiple localities', false) +
            handleDelimitedValues(place.localGovernmentAreas ? place.localGovernmentAreas : '', 2, ' (multiple local government areas)', true),
          id: place.id,
        }),
      )
    }
  }

  const handleDelimitedValues = (delimitedString: string, maximum: number, alternateValue: string, enclosed: boolean) => {
    if (delimitedString) {
      const splitString = delimitedString.split(',')
      if (splitString.length <= maximum) {
        if (enclosed) {
          return ' (' + delimitedString + ')'
        } else {
          return delimitedString
        }
      } else {
        return alternateValue
      }
    } else {
      return ''
    }
  }

  /**
   * semantic-ui-react Search callback
   *
   * Called when a result is selected
   *
   * @param e
   * @param result
   */
  const handleResultSelect = (e: React.MouseEvent<HTMLDivElement>, result: SearchResultData) => {
    let id = result.result.id

    let place = candidatePlaces.find(
      place => place.id === id,
    )

    // set the local state to the result
    dispatch(updatePlacenameSearchValue(place?.searchTerm))
    dispatch(resetPlacenameData())
    // set the url in the browser
    placenamesSearchContainer?.current?.getElementsByTagName('input')?.item(0)?.blur()
    history.push(`${LAYER_PLACE_NAMES_PATH}/${place?.placeId}`)
  }

  /**
   * semantic-ui-react Search callback
   *
   * Called for every item in the array of results to be rendered.
   *
   * @param props
   * @returns {*}
   */
  const ResultRenderer = ({ title, description }: SearchResultProps) => {
    return (
      <React.Fragment>
        <div className={'parcelSearchResultItem float-left'}>{title}</div>
        <div className={'description float-right'}>{description}</div>
      </React.Fragment>
    )
  }

  /**
   * semantic-ui-react Search callback
   *
   * Called when the input has changed
   *
   * @param e
   * @param value
   */
  const handleSearchChange = (e: React.MouseEvent<HTMLElement>, { value }: SearchProps) => {
    dispatch(updatePlacenameSearchValue(value))
    cancelPlacenameRequest()
    debouncedSearch.cancel()
    if (value && value.length >= 3) {
      debouncedSearch(value, phoneticSearch)
    } else if (!value) {
      onClickSearchClear()
    }
  }

  const showSearchResults = () => {
    setMinChar(0)
    placenamesSearchContainer?.current?.getElementsByTagName('input')?.item(0)?.focus()
  }

  const handlePhoneticSearchToggle = (e: React.MouseEvent<HTMLElement>, checked: CheckboxProps) => {
    setPhoneticSearch(!!checked.checked)
  }

  const handleCheckboxChange = (e: React.FormEvent<HTMLInputElement>, checked: CheckboxProps) => {
    if (searchValue) {
      placenamesSearchContainer?.current?.getElementsByTagName('input')?.item(0)?.focus()
      dispatch(updatePlacenameSearchValue(placenamesSearchContainer?.current?.getElementsByTagName('input')?.item(0)?.value))
      setPhoneticSearch(!!checked.checked)
      debouncedSearch(searchValue, !!checked.checked)

    }
  }

  const handleSearchValueClear = () => {
    dispatch(updateCandidatePlacesData([]))
    history.push(LAYER_PLACE_NAMES_PATH)
  }

  const onClickSearchClear = () => {
    dispatch(updatePlacenameSearchValue(''))
    dispatch(resetPlacenameData())
    dispatch(resetPlaceMapView(true))
    handleSearchValueClear()
  }


  let results = getResults(candidatePlaces)
  return (
    <CSSTransition
      appear={true}
      in={true}
      exit={false}
      timeout={2000}
      classNames="parcel-search-bar">

      <div className={'placenamesSearchContainer'} ref={placenamesSearchContainer}>
        <div className={'searchBar'}>
          <Search className={'placenamesSearch'}
                  placeholder="Enter a place name..."
                  loading={isLoading}
                  onResultSelect={handleResultSelect}
                  onSearchChange={handleSearchChange}
                  results={results}
                  resultRenderer={ResultRenderer}
                  size={'big'}
                  value={searchValue}
                  noResultsMessage="No place name found"
                  noResultsDescription="Enter the Place Name"
                  minCharacters={minChar}
                  input={<Input input={{ spellCheck: 'false', type: 'search' }}/>}
          />
          {(searchValue) &&
              <Popup content="Clear" size="mini"
                     trigger={<Icon className={'searchClearIcon'} size="large" style={{ color: '#959d9f' }} link
                                    name="close" onClick={onClickSearchClear}/>}/>}


        </div>
        <div className={'searchOption'}>
          <AdvancedSearchComponent showSearchResults={showSearchResults}/>
          <Checkbox
            className={'phoneticCheckbox'}
            name="phoneticSearch"
            label="Similar Sounding Names"
            checked={phoneticSearch}
            onClick={handlePhoneticSearchToggle}
            onChange={handleCheckboxChange}
          />
        </div>
      </div>

    </CSSTransition>
  )

}

export default PlacenamesSearchBar





